import React, { useState, useEffect, useCallback } from 'react';
import SafetyNote from './SafetyNote';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Alert,
  useTheme,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Shuffle,
  Visibility,
  Check,
  Close,
  RestartAlt,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import type { FlashcardDeck, Flashcard } from '../types/Flashcard';
import { flashcardService } from '../services/flashcardService';
import { preferencesManager } from '../utils/preferencesManager';
import { authService } from '../services/authService';
import { getFlashcardTypeLabel, splitMultiBlankQuestion } from '../utils/flashcardUtils';
import { shuffleArray } from '../utils/random';
import SignInPromptDialog from './SignInPromptDialog';

interface FlashcardPlayerProps {
  deck: FlashcardDeck;
  onClose: () => void;
}

const FlashcardPlayer: React.FC<FlashcardPlayerProps> = ({ deck, onClose }) => {
  const theme = useTheme();
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const [cards, setCards] = useState<Flashcard[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [selectedChoice, setSelectedChoice] = useState<number | null>(null);
  const [selectedChoices, setSelectedChoices] = useState<number[]>([]);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [correctedCardIds, setCorrectedCardIds] = useState<Set<string>>(new Set());
  const isRetryMode = deck.deckName.includes('Retry');
  const isRandom25Mode = deck.deckName.includes('Random 25 Cards');
  // Track shuffled choices and index mapping for current card
  const [shuffledChoices, setShuffledChoices] = useState<string[]>([]);
  const [choiceIndexMap, setChoiceIndexMap] = useState<number[]>([]);
  // Track user's ordering for Ordering questions
  const [userOrder, setUserOrder] = useState<number[]>([]); // Indices in user's chosen order
  const [selectedBlankChoices, setSelectedBlankChoices] = useState<number[]>([]);
  const [matchingPairs, setMatchingPairs] = useState<Map<number, number>>(new Map()); // termIdx -> shuffledDefIdx
  const [matchingSelectedTerm, setMatchingSelectedTerm] = useState<number | null>(null); // termIdx pending selection
  const [shuffledColumnB, setShuffledColumnB] = useState<string[]>([]);
  const [columnBIndexMap, setColumnBIndexMap] = useState<number[]>([]); // shuffledDefIdx -> original termIdx

  useEffect(() => {
    // Shuffle cards on mount
    shuffleCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck]);

  const shuffleCards = () => {
    const shuffled = shuffleArray(deck.cards);
    setCards(shuffled);
    setCurrentIndex(0);
    setShowAnswer(false);
    setSelectedChoice(null);
    setSelectedChoices([]);
    setIsCorrect(null);
    setCorrectCount(0);
    setIncorrectCount(0);
    setCompleted(false);
    setCorrectedCardIds(new Set());
    setSelectedBlankChoices([]);
    setMatchingPairs(new Map());
    setMatchingSelectedTerm(null);
    setShuffledColumnB([]);
    setColumnBIndexMap([]);
  };

  // Check if any choice references other answer letters (A, B, C, D, etc.)
  const hasAnswerReferences = (choices: string[]): boolean => {
    // Pattern to detect references like "A and B", "Both A and C", "Only A", etc.
    const referencePattern = /\b([Aa]|[Bb]|[Cc]|[Dd]|[Ee])\s+(and|or|,)\s+([Aa]|[Bb]|[Cc]|[Dd]|[Ee])|[Bb]oth\s+([Aa]|[Bb]|[Cc]|[Dd]|[Ee])|[Oo]nly\s+([Aa]|[Bb]|[Cc]|[Dd]|[Ee])/;

    return choices.some(choice => referencePattern.test(choice));
  };

  // Shuffle the choices for a given card, keeping "All of the above" at the end
  const shuffleChoicesForCard = useCallback((card: Flashcard) => {
    if (!card.choices || card.choices.length === 0) {
      setShuffledChoices([]);
      setChoiceIndexMap([]);
      return;
    }

    if (card.type === 'TrueFalse') {
      const normalizedChoiceEntries = card.choices
        .map((choice, index) => ({
          choice,
          index,
          normalized: choice.trim().toLowerCase(),
        }));
      const trueChoice = normalizedChoiceEntries.find((entry) => entry.normalized === 'true');
      const falseChoice = normalizedChoiceEntries.find((entry) => entry.normalized === 'false');

      if (trueChoice && falseChoice) {
        setShuffledChoices([trueChoice.choice, falseChoice.choice]);
        setChoiceIndexMap([trueChoice.index, falseChoice.index]);
        return;
      }
    }

    // Don't shuffle if choices reference other answer letters
    if (hasAnswerReferences(card.choices)) {
      setShuffledChoices(card.choices);
      setChoiceIndexMap(card.choices.map((_, i) => i));
      return;
    }

    // Identify "All of the above" type answers (case insensitive)
    const allOfTheAbovePattern = /all of the above|all of these|all these|all the above|none of the above/i;
    const allOfTheAboveIndices: number[] = [];
    const normalIndices: number[] = [];

    card.choices.forEach((choice, index) => {
      if (allOfTheAbovePattern.test(choice)) {
        allOfTheAboveIndices.push(index);
      } else {
        normalIndices.push(index);
      }
    });

    // Shuffle normal choices
    const shuffledNormalIndices = shuffleArray(normalIndices);

    // Combine shuffled normal + "all of the above" at the end
    const finalIndexOrder = [...shuffledNormalIndices, ...allOfTheAboveIndices];

    // Create shuffled choices array and index map
    const newShuffledChoices = finalIndexOrder.map(i => card.choices![i]);
    setShuffledChoices(newShuffledChoices);
    setChoiceIndexMap(finalIndexOrder);
  }, []);

  const currentCard = cards[currentIndex];
  const sourceDeckCount = new Set(cards.map((card) => card.sourceDeckId).filter(Boolean)).size;

  // Shuffle choices when the current card changes
  useEffect(() => {
    if (currentCard) {
      shuffleChoicesForCard(currentCard);
      // Initialize user order for Ordering questions
      if (currentCard.type === 'Ordering' && currentCard.choices) {
        setUserOrder(currentCard.choices.map((_, i) => i));
      } else {
        setUserOrder([]);
      }

      if (currentCard.type === 'MultiBlankChoice' && currentCard.choiceGroups) {
        setSelectedBlankChoices(currentCard.choiceGroups.map(() => -1));
      } else {
        setSelectedBlankChoices([]);
      }

      if (currentCard.type === 'Matching' && currentCard.choices && currentCard.choiceGroups?.[0]) {
        const defs = currentCard.choiceGroups[0];
        const indices = defs.map((_, i) => i);
        const shuffled = shuffleArray(indices);
        setShuffledColumnB(shuffled.map(i => defs[i]));
        setColumnBIndexMap(shuffled);
        setMatchingPairs(new Map());
        setMatchingSelectedTerm(null);
      } else {
        setShuffledColumnB([]);
        setColumnBIndexMap([]);
        setMatchingPairs(new Map());
        setMatchingSelectedTerm(null);
      }
    }
  }, [currentCard, shuffleChoicesForCard]);

  // In retry mode, calculate progress based on cards reviewed (including corrected ones)
  const cardsReviewed = isRetryMode
    ? currentIndex + 1 + correctedCardIds.size
    : currentIndex + 1;
  const progress = (cardsReviewed / cards.length) * 100;

  const handleNext = () => {
    // In retry mode, skip cards that have been corrected in this session
    let nextIndex = currentIndex + 1;

    if (isRetryMode) {
      while (nextIndex < cards.length && correctedCardIds.has(cards[nextIndex].cardId)) {
        nextIndex++;
      }
    }

    if (nextIndex < cards.length) {
      setCurrentIndex(nextIndex);
      setShowAnswer(false);
      setShowHint(false);
      setSelectedChoice(null);
      setSelectedChoices([]);
      setIsCorrect(null);
      setUserOrder([]);
      setSelectedBlankChoices([]);
      setMatchingPairs(new Map());
      setMatchingSelectedTerm(null);
    } else {
      setCompleted(true);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowAnswer(false);
      setShowHint(false);
      setSelectedChoice(null);
      setSelectedChoices([]);
      setIsCorrect(null);
      setUserOrder([]);
      setSelectedBlankChoices([]);
      setMatchingPairs(new Map());
      setMatchingSelectedTerm(null);
    }
  };

  const handleShowAnswer = () => {
    setShowAnswer(true);
  };

  const recordAnswer = async (isCorrect: boolean) => {
    // Only record answers for authenticated users — guests have no persistent account
    if (!authService.isAuthenticated()) return;
    const userId = preferencesManager.getUserId() || preferencesManager.getDemoUserId();
    flashcardService.recordAnswer({
      userId,
      deckId: currentCard.sourceDeckId || deck.deckId,
      cardId: currentCard.sourceCardId || currentCard.cardId,
      isCorrect
    });
  };

  const handleMarkCorrect = () => {
    setCorrectCount(correctCount + 1);
    setIsCorrect(true);
    recordAnswer(true);

    // In retry mode, mark this card as corrected so it's skipped
    if (isRetryMode) {
      setCorrectedCardIds(prev => new Set(prev).add(currentCard.cardId));
    }

    setTimeout(handleNext, 500);
  };

  const handleMarkIncorrect = () => {
    setIncorrectCount(incorrectCount + 1);
    setIsCorrect(false);
    recordAnswer(false);
    setTimeout(handleNext, 500);
  };

  // Determine if the current card is multi-select
  const isMultiSelect = () => {
    const correctIndices = currentCard.correctChoiceIndices ||
      (currentCard.correctChoiceIndex !== undefined ? [currentCard.correctChoiceIndex] : []);
    return correctIndices.length > 1;
  };

  // Handle single-select multiple choice (radio buttons)
  const handleSingleChoiceSelect = (shuffledIndex: number) => {
    setSelectedChoice(shuffledIndex);

    // Map shuffled index back to original index
    const originalIndex = choiceIndexMap[shuffledIndex];

    // Check both new (CorrectChoiceIndices) and old (correctChoiceIndex) fields
    const correctIndices = currentCard.correctChoiceIndices ||
      (currentCard.correctChoiceIndex !== undefined ? [currentCard.correctChoiceIndex] : []);

    const correct = correctIndices.includes(originalIndex);
    setIsCorrect(correct);
    if (correct) {
      setCorrectCount(correctCount + 1);
      // In retry mode, mark this card as corrected so it's skipped
      if (isRetryMode) {
        setCorrectedCardIds(prev => new Set(prev).add(currentCard.cardId));
      }
    } else {
      setIncorrectCount(incorrectCount + 1);
    }
    recordAnswer(correct);
    setShowAnswer(true); // Show which answers are correct
  };

  // Handle multi-select checkbox toggle
  const handleMultiChoiceToggle = (choiceIndex: number) => {
    if (selectedChoices.includes(choiceIndex)) {
      setSelectedChoices(selectedChoices.filter(i => i !== choiceIndex));
    } else {
      setSelectedChoices([...selectedChoices, choiceIndex]);
    }
  };

  // Submit multi-select answer
  const handleMultiChoiceSubmit = () => {
    const correctIndices = currentCard.correctChoiceIndices ||
      (currentCard.correctChoiceIndex !== undefined ? [currentCard.correctChoiceIndex] : []);

    // Map shuffled indices back to original indices
    const originalSelectedIndices = selectedChoices.map(shuffledIdx => choiceIndexMap[shuffledIdx]);

    // Check if selected choices match exactly with correct indices
    const selectedSet = new Set(originalSelectedIndices);
    const correctSet = new Set(correctIndices);

    const correct =
      selectedSet.size === correctSet.size &&
      Array.from(selectedSet).every(i => correctSet.has(i));

    setIsCorrect(correct);
    if (correct) {
      setCorrectCount(correctCount + 1);
      // In retry mode, mark this card as corrected so it's skipped
      if (isRetryMode) {
        setCorrectedCardIds(prev => new Set(prev).add(currentCard.cardId));
      }
    } else {
      setIncorrectCount(incorrectCount + 1);
    }
    recordAnswer(correct);
    setShowAnswer(true);
  };

  // Move item up in user's ordering
  const handleMoveUp = (index: number) => {
    if (index > 0) {
      const newOrder = [...userOrder];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      setUserOrder(newOrder);
    }
  };

  // Move item down in user's ordering
  const handleMoveDown = (index: number) => {
    if (index < userOrder.length - 1) {
      const newOrder = [...userOrder];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      setUserOrder(newOrder);
    }
  };

  // Submit ordering answer
  const handleOrderingSubmit = () => {
    const correctIndices = currentCard.correctChoiceIndices || [];

    // Check if user's order matches correct order
    const correct = userOrder.every((val, idx) => val === correctIndices[idx]);

    setIsCorrect(correct);
    if (correct) {
      setCorrectCount(correctCount + 1);
      // In retry mode, mark this card as corrected so it's skipped
      if (isRetryMode) {
        setCorrectedCardIds(prev => new Set(prev).add(currentCard.cardId));
      }
    } else {
      setIncorrectCount(incorrectCount + 1);
    }
    recordAnswer(correct);
    setShowAnswer(true);
  };

  const handleMatchingTermClick = (termIdx: number) => {
    if (showAnswer) return;
    // If this term is already paired, unpair it first
    if (matchingPairs.has(termIdx)) {
      setMatchingPairs(prev => { const m = new Map(prev); m.delete(termIdx); return m; });
      setMatchingSelectedTerm(termIdx);
      return;
    }
    setMatchingSelectedTerm(prev => prev === termIdx ? null : termIdx);
  };

  const handleMatchingDefClick = (shuffledDefIdx: number) => {
    if (showAnswer) return;
    // If no term selected, check if this def is already paired and unpair it
    if (matchingSelectedTerm === null) {
      const pairedTermEntry = Array.from(matchingPairs.entries()).find(([, v]) => v === shuffledDefIdx);
      if (pairedTermEntry) {
        setMatchingPairs(prev => { const m = new Map(prev); m.delete(pairedTermEntry[0]); return m; });
      }
      return;
    }
    // Unpair any existing def that was paired to this def slot
    const existingTermForDef = Array.from(matchingPairs.entries()).find(([, v]) => v === shuffledDefIdx);
    setMatchingPairs(prev => {
      const m = new Map(prev);
      if (existingTermForDef) m.delete(existingTermForDef[0]);
      m.set(matchingSelectedTerm, shuffledDefIdx);
      return m;
    });
    setMatchingSelectedTerm(null);
  };

  const handleMatchingSubmit = () => {
    if (!currentCard.choices || !currentCard.choiceGroups?.[0]) return;
    const allCorrect = Array.from(matchingPairs.entries()).every(([termIdx, shuffledDefIdx]) =>
      columnBIndexMap[shuffledDefIdx] === termIdx
    );
    setIsCorrect(allCorrect);
    if (allCorrect) {
      setCorrectCount(correctCount + 1);
      if (isRetryMode) setCorrectedCardIds(prev => new Set(prev).add(currentCard.cardId));
    } else {
      setIncorrectCount(incorrectCount + 1);
    }
    recordAnswer(allCorrect);
    setShowAnswer(true);
  };

  const handleBlankChoiceChange = (blankIndex: number, choiceIndex: number) => {
    setSelectedBlankChoices(prev => prev.map((currentChoice, currentBlankIndex) => {
      return currentBlankIndex === blankIndex ? choiceIndex : currentChoice;
    }));
  };

  const handleMultiBlankSubmit = () => {
    const correctIndices = currentCard.correctChoiceGroupIndices || [];
    const correct =
      selectedBlankChoices.length === correctIndices.length &&
      selectedBlankChoices.every((choiceIndex, blankIndex) => choiceIndex === correctIndices[blankIndex]);

    setIsCorrect(correct);
    if (correct) {
      setCorrectCount(correctCount + 1);
      if (isRetryMode) {
        setCorrectedCardIds(prev => new Set(prev).add(currentCard.cardId));
      }
    } else {
      setIncorrectCount(incorrectCount + 1);
    }
    recordAnswer(correct);
    setShowAnswer(true);
  };

  const handleRestart = () => {
    shuffleCards();
  };

  if (!currentCard || cards.length === 0) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, margin: '0 auto' }}>
        <Alert severity="warning">No flashcards available in this deck.</Alert>
        <Button onClick={onClose} sx={{ mt: 2 }}>
          Back to Decks
        </Button>
      </Box>
    );
  }

  if (completed) {
    const totalAttempted = correctCount + incorrectCount;
    const score = totalAttempted > 0 ? Math.round((correctCount / totalAttempted) * 100) : 0;

    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, margin: '0 auto', textAlign: 'center' }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            Study Session Complete!
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {deck.deckName}
          </Typography>
          {isRandom25Mode && (
            <Typography variant="body2" color="text.secondary">
              Next time you start this mode, it will deal a fresh mix from {sourceDeckCount} decks.
            </Typography>
          )}

          <Box sx={{ my: 4 }}>
            <Typography variant="h2" color="primary" gutterBottom>
              {score}%
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {correctCount} correct out of {totalAttempted} cards
            </Typography>
          </Box>

          {!authService.isAuthenticated() && (
            <Alert
              severity="info"
              sx={{ mb: 3, textAlign: 'left' }}
              action={
                <Button color="inherit" size="small" onClick={() => setShowSignInPrompt(true)}>
                  Sign in
                </Button>
              }
            >
              Your score wasn't saved. Sign in to track missed cards and retry them later.
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="outlined" onClick={onClose} startIcon={<ArrowBack />}>
              Back to Decks
            </Button>
            <Button variant="contained" onClick={handleRestart} startIcon={<RestartAlt />}>
              Study Again
            </Button>
          </Box>
        </Paper>

        <SignInPromptDialog
          open={showSignInPrompt}
          onClose={() => setShowSignInPrompt(false)}
          action="Saving your flashcard progress"
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, margin: '0 auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))'
            : 'linear-gradient(180deg, rgba(25,118,210,0.06), rgba(255,255,255,0.8))',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={onClose}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ textAlign: 'center', px: 2 }}>
            {deck.deckName}
          </Typography>
          <IconButton onClick={shuffleCards}>
            <Shuffle />
          </IconButton>
        </Box>
        <SafetyNote kind="studyAid" sx={{ textAlign: 'center', mb: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          {isRandom25Mode && <Chip label="Fresh live draw" size="small" color="primary" variant="outlined" />}
          {isRandom25Mode && <Chip label={`${sourceDeckCount} source decks`} size="small" color="secondary" variant="outlined" />}
          <Chip label={`Card ${currentIndex + 1} of ${cards.length}`} size="small" variant="outlined" />
          <Chip label={`${correctCount} correct`} size="small" color="success" variant="outlined" />
          <Chip label={`${incorrectCount} missed`} size="small" color="error" variant="outlined" />
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 999 }} />
      </Paper>

      <Card
        sx={{
          minHeight: 300,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          mb: 3,
          borderRadius: 4,
          boxShadow: theme.palette.mode === 'dark' ? 2 : 6,
          border: '1px solid',
          borderColor: isCorrect === true
            ? 'success.light'
            : isCorrect === false
              ? 'error.light'
              : 'divider',
          bgcolor:
            isCorrect === true
              ? theme.palette.mode === 'dark'
                ? 'rgba(76, 175, 80, 0.2)'
                : 'rgba(76, 175, 80, 0.1)'
              : isCorrect === false
              ? theme.palette.mode === 'dark'
                ? 'rgba(244, 67, 54, 0.2)'
                : 'rgba(244, 67, 54, 0.1)'
              : undefined,
        }}
      >
        <CardContent sx={{ p: currentCard.imageUrl ? { xs: 1.5, sm: 2.5 } : { xs: 2.5, sm: 4 } }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip
              label={getFlashcardTypeLabel(currentCard)}
              size="small"
              color="primary"
              variant="outlined"
            />
            {currentCard.sourceDeckName && (
              <Chip
                label={`From: ${currentCard.sourceDeckName}`}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
          </Box>

          {currentCard.type === 'MultiBlankChoice' ? (
            <Box sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose the best answer for each blank.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                {splitMultiBlankQuestion(currentCard.question).map((segment, segmentIndex, segments) => (
                  <React.Fragment key={segmentIndex}>
                    {segment && (
                      <Typography variant="h5" component="span">
                        {segment}
                      </Typography>
                    )}
                    {segmentIndex < segments.length - 1 && (
                      <Select
                        size="small"
                        value={selectedBlankChoices[segmentIndex] ?? -1}
                        displayEmpty
                        disabled={showAnswer}
                        onChange={(e) => handleBlankChoiceChange(segmentIndex, Number(e.target.value))}
                        sx={{
                          minWidth: 160,
                          bgcolor: showAnswer
                            ? selectedBlankChoices[segmentIndex] === currentCard.correctChoiceGroupIndices?.[segmentIndex]
                              ? 'rgba(76, 175, 80, 0.1)'
                              : 'rgba(244, 67, 54, 0.1)'
                            : undefined,
                        }}
                      >
                        <MenuItem value={-1} disabled>Select answer</MenuItem>
                        {(currentCard.choiceGroups?.[segmentIndex] || []).map((choice, choiceIndex) => (
                          <MenuItem key={choiceIndex} value={choiceIndex}>{choice}</MenuItem>
                        ))}
                      </Select>
                    )}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          ) : (
            <Typography variant="h5" gutterBottom sx={{ my: 3, lineHeight: 1.35 }}>
              {currentCard.question}
            </Typography>
          )}

          {/* Hint section */}
          {currentCard.hint && (
            <Box sx={{ mb: 2 }}>
              {!showHint ? (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowHint(true)}
                  sx={{ mb: 1 }}
                >
                  💡 Show Hint
                </Button>
              ) : (
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Hint:</strong> {currentCard.hint}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {currentCard.imageUrl && (
            <Box
              sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <img
                src={currentCard.imageUrl}
                alt="Reference material"
                style={{
                  display: 'block',
                  width: 'auto',
                  maxWidth: '100%',
                  maxHeight: '52vh',
                  height: 'auto',
                  objectFit: 'contain',
                  objectPosition: 'center',
                  margin: '0 auto',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                }}
              />
            </Box>
          )}

          {currentCard.type === 'Ordering' ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Arrange the items in the correct order:
              </Typography>
              {currentCard.choices && userOrder.map((choiceIndex, position) => {
                const correctIndices = currentCard.correctChoiceIndices || [];
                const isCorrectPosition = showAnswer && correctIndices[position] === choiceIndex;
                const isWrongPosition = showAnswer && correctIndices[position] !== choiceIndex;

                return (
                  <Paper
                    key={position}
                    sx={{
                      p: 2,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: isCorrectPosition
                        ? 'rgba(76, 175, 80, 0.1)'
                        : isWrongPosition
                          ? 'rgba(244, 67, 54, 0.1)'
                          : undefined,
                    }}
                  >
                    <Typography sx={{ mr: 2, minWidth: 30, fontWeight: 'bold' }}>
                      {position + 1}.
                    </Typography>
                    <Typography sx={{ flexGrow: 1 }}>
                      {currentCard.choices?.[choiceIndex]}
                    </Typography>
                    {!showAnswer && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveUp(position)}
                          disabled={position === 0}
                        >
                          <ArrowUpward />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveDown(position)}
                          disabled={position === userOrder.length - 1}
                        >
                          <ArrowDownward />
                        </IconButton>
                      </Box>
                    )}
                  </Paper>
                );
              })}
              {!showAnswer && (
                <Button
                  variant="contained"
                  startIcon={<Check />}
                  onClick={handleOrderingSubmit}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Submit Answer
                </Button>
              )}
              {showAnswer && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Correct order:</strong>
                    {currentCard.choices && currentCard.correctChoiceIndices?.map((idx, pos) => (
                      <div key={pos}>{pos + 1}. {currentCard.choices![idx]}</div>
                    ))}
                  </Typography>
                </Alert>
              )}
            </Box>
          ) : currentCard.type === 'MultiBlankChoice' ? (
            <Box>
              {!showAnswer && (
                <Button
                  variant="contained"
                  startIcon={<Check />}
                  onClick={handleMultiBlankSubmit}
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled={selectedBlankChoices.length === 0 || selectedBlankChoices.some(choice => choice < 0)}
                >
                  Submit Answer
                </Button>
              )}
              {showAnswer && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Correct completion:</strong>
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {splitMultiBlankQuestion(currentCard.question).map((segment, segmentIndex, segments) => (
                      <React.Fragment key={segmentIndex}>
                        {segment && <Typography component="span" variant="body2">{segment}</Typography>}
                        {segmentIndex < segments.length - 1 && (
                          <Typography component="span" variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {(currentCard.choiceGroups?.[segmentIndex] || [])[currentCard.correctChoiceGroupIndices?.[segmentIndex] || 0]}
                          </Typography>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </Alert>
              )}
            </Box>
          ) : (currentCard.type === 'MultipleChoice' || currentCard.type === 'TrueFalse') ? (
            <>
              {isMultiSelect() ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Select all that apply:
                  </Typography>
                  <FormGroup>
                    {shuffledChoices.map((choice, shuffledIndex) => {
                      // Get correct answer indices (in original order)
                      const correctIndices = currentCard.correctChoiceIndices ||
                        (currentCard.correctChoiceIndex !== undefined ? [currentCard.correctChoiceIndex] : []);

                      // Map shuffled index to original index
                      const originalIndex = choiceIndexMap[shuffledIndex];
                      const isCorrectChoice = correctIndices.includes(originalIndex);
                      const isSelectedChoice = selectedChoices.includes(shuffledIndex);

                      // Determine background color
                      let bgcolor = undefined;
                      if (showAnswer && isCorrectChoice) {
                        // Show all correct answers in green when answer is shown
                        bgcolor = 'rgba(76, 175, 80, 0.1)';
                      } else if (showAnswer && isSelectedChoice && !isCorrectChoice) {
                        // Show selected incorrect answer in red
                        bgcolor = 'rgba(244, 67, 54, 0.1)';
                      }

                      return (
                        <FormControlLabel
                          key={shuffledIndex}
                          control={
                            <Checkbox
                              checked={isSelectedChoice}
                              onChange={() => handleMultiChoiceToggle(shuffledIndex)}
                              disabled={showAnswer}
                            />
                          }
                          label={`${String.fromCharCode(65 + shuffledIndex)}) ${choice}`}
                          sx={{
                            bgcolor,
                            borderRadius: 1,
                            px: 1,
                            mb: 1,
                          }}
                        />
                      );
                    })}
                  </FormGroup>
                  {!showAnswer && (
                    <Button
                      variant="contained"
                      startIcon={<Check />}
                      onClick={handleMultiChoiceSubmit}
                      fullWidth
                      sx={{ mt: 2 }}
                      disabled={selectedChoices.length === 0}
                    >
                      Submit Answer
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <RadioGroup value={selectedChoice} onChange={(e) => handleSingleChoiceSelect(parseInt(e.target.value))}>
                    {shuffledChoices.map((choice, shuffledIndex) => {
                      // Get correct answer indices (support both old and new fields, in original order)
                      const correctIndices = currentCard.correctChoiceIndices ||
                        (currentCard.correctChoiceIndex !== undefined ? [currentCard.correctChoiceIndex] : []);

                      // Map shuffled index to original index
                      const originalIndex = choiceIndexMap[shuffledIndex];
                      const isCorrectChoice = correctIndices.includes(originalIndex);
                      const isSelectedChoice = selectedChoice === shuffledIndex;

                      // Determine background color
                      let bgcolor = undefined;
                      if (showAnswer && isCorrectChoice) {
                        // Show all correct answers in green when answer is shown
                        bgcolor = 'rgba(76, 175, 80, 0.1)';
                      } else if (showAnswer && isSelectedChoice && !isCorrectChoice) {
                        // Show selected incorrect answer in red
                        bgcolor = 'rgba(244, 67, 54, 0.1)';
                      }

                      return (
                        <FormControlLabel
                          key={shuffledIndex}
                          value={shuffledIndex}
                          control={<Radio />}
                          label={`${String.fromCharCode(65 + shuffledIndex)}) ${choice}`}
                          disabled={showAnswer}
                          sx={{
                            bgcolor,
                            borderRadius: 1,
                            px: 1,
                            mb: 1,
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                  {!showAnswer && (
                    <Button variant="outlined" startIcon={<Visibility />} onClick={handleShowAnswer} fullWidth sx={{ mt: 2 }}>
                      Show Answer
                    </Button>
                  )}
                </>
              )}
            </>
          ) : currentCard.type === 'Matching' ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {showAnswer
                  ? 'Results:'
                  : matchingSelectedTerm !== null
                    ? 'Now tap a definition to match it.'
                    : 'Tap a term, then tap its matching definition.'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                {/* Column A — Terms */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                    TERMS
                  </Typography>
                  {(currentCard.choices || []).map((term, termIdx) => {
                    const pairedShuffledDefIdx = matchingPairs.get(termIdx);
                    const isPaired = pairedShuffledDefIdx !== undefined;
                    const isSelected = matchingSelectedTerm === termIdx;
                    const isCorrectPair = showAnswer && isPaired && columnBIndexMap[pairedShuffledDefIdx!] === termIdx;
                    const isWrongPair = showAnswer && isPaired && columnBIndexMap[pairedShuffledDefIdx!] !== termIdx;

                    return (
                      <Paper
                        key={termIdx}
                        onClick={() => handleMatchingTermClick(termIdx)}
                        elevation={isSelected ? 4 : 1}
                        sx={{
                          p: 1.5,
                          cursor: showAnswer ? 'default' : 'pointer',
                          border: '2px solid',
                          borderColor: isCorrectPair
                            ? 'success.main'
                            : isWrongPair
                              ? 'error.main'
                              : isSelected
                                ? 'primary.main'
                                : isPaired
                                  ? 'primary.light'
                                  : 'divider',
                          bgcolor: isCorrectPair
                            ? 'rgba(76,175,80,0.1)'
                            : isWrongPair
                              ? 'rgba(244,67,54,0.1)'
                              : isSelected
                                ? theme.palette.mode === 'dark' ? 'rgba(144,202,249,0.15)' : 'rgba(25,118,210,0.08)'
                                : isPaired
                                  ? theme.palette.mode === 'dark' ? 'rgba(144,202,249,0.07)' : 'rgba(25,118,210,0.04)'
                                  : 'background.paper',
                          transition: 'border-color 0.15s, background-color 0.15s',
                          '&:hover': showAnswer ? {} : { borderColor: 'primary.main' },
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
                          {term}
                        </Typography>
                        {isPaired && !showAnswer && (
                          <Typography variant="caption" color="primary.main" sx={{ mt: 0.5, display: 'block' }}>
                            → {shuffledColumnB[pairedShuffledDefIdx!]}
                          </Typography>
                        )}
                        {showAnswer && isPaired && (
                          <Typography variant="caption" color={isCorrectPair ? 'success.main' : 'error.main'} sx={{ mt: 0.5, display: 'block' }}>
                            {isCorrectPair ? '✓ Correct' : `✗ Should be: ${currentCard.choiceGroups?.[0]?.[termIdx]}`}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })}
                </Box>

                {/* Column B — Definitions */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                    DEFINITIONS
                  </Typography>
                  {shuffledColumnB.map((def, shuffledDefIdx) => {
                    const pairedTermEntry = Array.from(matchingPairs.entries()).find(([, v]) => v === shuffledDefIdx);
                    const isPaired = pairedTermEntry !== undefined;
                    const isActiveTarget = matchingSelectedTerm !== null && !isPaired;

                    return (
                      <Paper
                        key={shuffledDefIdx}
                        onClick={() => handleMatchingDefClick(shuffledDefIdx)}
                        elevation={1}
                        sx={{
                          p: 1.5,
                          cursor: showAnswer ? 'default' : 'pointer',
                          border: '2px solid',
                          borderColor: isPaired ? 'primary.light' : isActiveTarget ? 'primary.main' : 'divider',
                          bgcolor: isPaired
                            ? theme.palette.mode === 'dark' ? 'rgba(144,202,249,0.07)' : 'rgba(25,118,210,0.04)'
                            : 'background.paper',
                          opacity: showAnswer ? 1 : isPaired ? 0.6 : 1,
                          transition: 'border-color 0.15s, background-color 0.15s, opacity 0.15s',
                          '&:hover': showAnswer || isPaired ? {} : { borderColor: 'primary.main', bgcolor: theme.palette.mode === 'dark' ? 'rgba(144,202,249,0.07)' : 'rgba(25,118,210,0.04)' },
                        }}
                      >
                        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                          {def}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>

              {!showAnswer && (
                <Button
                  variant="contained"
                  startIcon={<Check />}
                  onClick={handleMatchingSubmit}
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled={matchingPairs.size < (currentCard.choices?.length ?? 0)}
                >
                  Submit Matches
                </Button>
              )}
            </Box>
          ) : (
            <Box>
              {showAnswer ? (
                <Paper sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                  <Typography variant="body1">{currentCard.answer}</Typography>
                </Paper>
              ) : (
                <Button variant="outlined" startIcon={<Visibility />} onClick={handleShowAnswer} fullWidth>
                  Show Answer
                </Button>
              )}
            </Box>
          )}

          {isCorrect !== null && (
            <Alert
              severity={isCorrect ? 'success' : 'error'}
              sx={{ mt: 2 }}
              icon={isCorrect ? <Check /> : <Close />}
            >
              {isCorrect ? 'Correct!' : 'Incorrect'}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button onClick={handlePrevious} disabled={currentIndex === 0} startIcon={<ArrowBack />}>
          Previous
        </Button>

        {currentCard.type === 'QuestionAnswer' && showAnswer && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" color="error" startIcon={<Close />} onClick={handleMarkIncorrect}>
              I was wrong
            </Button>
            <Button variant="contained" color="success" startIcon={<Check />} onClick={handleMarkCorrect}>
              I was right
            </Button>
          </Box>
        )}

        <Button
          onClick={handleNext}
          disabled={(currentCard.type === 'QuestionAnswer' || currentCard.type === 'Matching') && !showAnswer}
          endIcon={<ArrowForward />}
        >
          {currentIndex === cards.length - 1 ? 'Finish' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

export default FlashcardPlayer;
